import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../api/client';

const emptyDraft = () => ({ key: '', clearKey: false });

export default function AiSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState('');
  const [fields, setFields] = useState({});
  const [drafts, setDrafts] = useState({});
  const [modelLists, setModelLists] = useState({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);

  const apply = (data) => {
    setSettings(data);
    setProvider(data.provider || '');
    setFields(Object.fromEntries(Object.entries(data.providers).map(([id, p]) => [id, { model: p.model, baseUrl: p.baseUrl }])));
    setDrafts({});
  };

  useEffect(() => {
    apiGet('/admin/ai/settings').then(apply).catch((err) => setMessage({ type: 'error', text: err.message }));
  }, []);

  const providers = useMemo(() => settings?.providers || {}, [settings]);
  // The panel edits the chosen provider, or the one Automatic picked.
  const editId = provider || settings?.active.provider || 'openai';
  const info = providers[editId];
  const field = fields[editId] || { model: '', baseUrl: '' };
  const draft = drafts[editId] || emptyDraft();

  const setField = (patch) => setFields((current) => ({ ...current, [editId]: { ...field, ...patch } }));
  const setDraft = (patch) => setDrafts((current) => ({ ...current, [editId]: { ...draft, ...patch } }));

  const dirty = useMemo(() => {
    if (!settings) return false;
    if (provider !== (settings.provider || '')) return true;
    return Object.entries(providers).some(([id, p]) => {
      const f = fields[id] || {};
      const d = drafts[id] || {};
      return (f.model || '') !== (p.model || '') || (f.baseUrl || '') !== (p.baseUrl || '') || d.key || d.clearKey;
    });
  }, [settings, provider, providers, fields, drafts]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = Object.fromEntries(Object.keys(providers).map((id) => [id, { ...(fields[id] || {}), ...(drafts[id] || {}) }]));
      apply(await apiPut('/admin/ai/settings', { provider, providers: payload }));
      setMessage({ type: 'ok', text: 'AI settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const loadModels = async () => {
    setLoadingModels(true);
    setMessage(null);
    try {
      const { models } = await apiGet(`/admin/ai/models?provider=${editId}`);
      setModelLists((current) => ({ ...current, [editId]: models }));
      setMessage({ type: 'ok', text: `${models.length} models available from ${info.label}. Start typing in Model to search.` });
    } catch (err) {
      setMessage({ type: 'error', text: `Could not load models: ${err.message}` });
    } finally {
      setLoadingModels(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const result = await apiPost('/admin/ai/test');
      setMessage({ type: 'ok', text: `Connected: ${providers[result.provider]?.label} answered with ${result.model} in ${(result.ms / 1000).toFixed(1)}s.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (!settings || !info) {
    return (
      <div className="admin-settings-card admin-settings-card--wide">
        <h3><i className="fas fa-magic" aria-hidden="true" /> AI writer</h3>
        <p>{message?.text || 'Loading…'}</p>
      </div>
    );
  }

  const active = settings.active;
  const isCustom = editId === 'custom';
  const keyedProviders = Object.entries(providers).filter(([, p]) => p.keySet);
  const modelOptions = modelLists[editId] || [];
  const savedKeyNeedsSave = draft.key !== '';

  return (
    <div className="admin-settings-card admin-settings-card--wide admin-ai-card">
      <h3><i className="fas fa-magic" aria-hidden="true" /> AI writer</h3>
      <p className="admin-status-row">
        <span className={`admin-status-dot admin-status-dot--${active.configured ? 'ok' : 'unknown'}`} />
        {active.configured
          ? <>Using <strong>{providers[active.provider]?.label}</strong> with <code>{active.model}</code></>
          : 'Not set up: choose a provider and add its API key.'}
      </p>

      <div className="admin-ai-layout">
        <label className="admin-field">
          <span>Provider</span>
          <select value={provider} onChange={(e) => { setProvider(e.target.value); setShowAdvanced(false); setMessage(null); }}>
            <option value="">Automatic (first provider with a key)</option>
            {Object.entries(providers).map(([id, p]) => (
              <option key={id} value={id}>{p.label}{p.keySet ? ' ✓' : ''}</option>
            ))}
          </select>
        </label>

        {keyedProviders.length > 0 && (
          <div className="admin-ai-keyed">
            <span>Keys saved:</span>
            {keyedProviders.map(([id, p]) => (
              <button
                key={id}
                type="button"
                className={`admin-pill ${id === active.provider ? 'is-green' : 'is-muted'}`}
                onClick={() => { setProvider(id); setMessage(null); }}
                title={p.keySource === 'env' ? `From api/.env (${p.envName})` : 'Saved in admin'}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="admin-ai-provider-block is-active">
        <div className="admin-ai-provider-head">
          <strong>{info.label}</strong>
          {!provider && <span className="admin-pill is-muted">Picked automatically</span>}
        </div>

        {(isCustom || showAdvanced) && (
          <label className="admin-field">
            <span>Base URL {isCustom ? '(OpenAI-compatible, ending in /v1)' : ''}</span>
            <input
              value={field.baseUrl || ''}
              onChange={(e) => setField({ baseUrl: e.target.value })}
              placeholder={info.defaultBaseUrl || 'https://your-server.example.com/v1'}
            />
          </label>
        )}

        <div className="admin-ai-row">
          <label className="admin-field">
            <span>API key</span>
            <input
              type="password"
              autoComplete="off"
              value={draft.key}
              onChange={(e) => setDraft({ key: e.target.value, clearKey: false })}
              placeholder={
                draft.clearKey ? 'Will be removed on save'
                  : info.keySource === 'admin' ? `Saved ${info.keyMask} (type to replace)`
                    : info.keySource === 'env' ? `From api/.env ${info.keyMask} (type to override)`
                      : `Paste key${info.keyHint ? ` (${info.keyHint})` : ''}${isCustom ? ' — optional for local servers' : ''}`
              }
            />
          </label>
          <label className="admin-field">
            <span>Model</span>
            <input
              list={`ai-models-${editId}`}
              value={field.model || ''}
              onChange={(e) => setField({ model: e.target.value })}
              placeholder={info.effectiveModel ? `${info.effectiveModel}${info.model ? '' : ' (default)'}` : 'Model ID'}
            />
            <datalist id={`ai-models-${editId}`}>
              {modelOptions.map((model) => <option key={model} value={model} />)}
            </datalist>
          </label>
        </div>

        <div className="admin-ai-links">
          <button
            type="button"
            className="admin-link-btn"
            onClick={loadModels}
            disabled={loadingModels || savedKeyNeedsSave || (!info.keySet && !isCustom)}
            title={savedKeyNeedsSave ? 'Save the key first' : undefined}
          >
            <i className={`fas fa-list${loadingModels ? ' fa-spin' : ''}`} aria-hidden="true" /> {loadingModels ? 'Loading models…' : 'Load models from provider'}
          </button>
          {info.keySource === 'admin' && !draft.key && (
            <button type="button" className="admin-link-btn" onClick={() => setDraft({ clearKey: !draft.clearKey })}>
              {draft.clearKey ? 'Keep saved key' : info.envKeySet ? 'Remove saved key (fall back to api/.env)' : 'Remove saved key'}
            </button>
          )}
          {!isCustom && (
            <button type="button" className="admin-link-btn" onClick={() => setShowAdvanced((v) => !v)}>
              {showAdvanced ? 'Hide advanced' : 'Advanced: change base URL'}
            </button>
          )}
        </div>
      </div>

      {message && <p className={`admin-ai-message admin-ai-message--${message.type}`}>{message.text}</p>}

      <div className="admin-ai-actions">
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save AI settings'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={test} disabled={testing || dirty || !active.configured} title={dirty ? 'Save first' : undefined}>
          <i className={`fas fa-plug${testing ? ' fa-spin' : ''}`} aria-hidden="true" /> {testing ? 'Testing…' : 'Test connection'}
        </button>
      </div>
      <p className="admin-form-hint">
        Used by Reviews → AI writer and Tool Pages → Generate with AI. Drafts are never published automatically.
        Keys are stored on the server, never shown again in full, and left out of backups and exports.
      </p>
    </div>
  );
}
